import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api, AppConfig, errorMessage, Wallet } from "../lib/api";

interface SavedAddress { id: string; name: string; address1: string; city: string; postcode: string }
import { money } from "../lib/format";
import { useBasket } from "../context/BasketContext";

const emptyAddress = { name: "", address1: "", city: "", postcode: "" };

export function BasketDrawer() {
  const { lines, setQty, remove, clear, total: gross, open, setOpen } = useBasket();
  const [address, setAddress] = useState(() => {
    try { return { ...emptyAddress, ...JSON.parse(localStorage.getItem("cellar_address") || "{}") }; } catch { return emptyAddress; }
  });
  const [placing, setPlacing] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: wallet } = useQuery<Wallet>({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data), enabled: open });
  const { data: config } = useQuery<AppConfig>({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data), enabled: open });
  const [cardBusy, setCardBusy] = useState(false);
  const { data: saved } = useQuery<SavedAddress[]>({ queryKey: ["addresses"], queryFn: () => api.get("/addresses").then((r) => r.data), enabled: open });
  const removeSaved = async (id: string) => { await api.delete(`/addresses/${id}`); qc.invalidateQueries({ queryKey: ["addresses"] }); };

  useEffect(() => { localStorage.setItem("cellar_address", JSON.stringify(address)); }, [address]);

  const { data: clearance } = useQuery<{ discount_pence: number; reason: string }>({ queryKey: ["clearance-quote", lines.map((l) => `${l.product_handle}:${l.quantity}`).join(",")], queryFn: () => api.post("/make-case/quote", { items: lines }).then((r) => r.data), enabled: open && lines.length > 0, staleTime: 60000 });
  const saving = clearance?.discount_pence || 0;
  const total = gross - saving;
  const balance = wallet?.balance_pence ?? 0;
  const shortfall = Math.max(0, total - balance);
  const [method, setMethod] = useState<"delivery" | "collection">(() => (localStorage.getItem("cellar_delivery") === "collection" ? "collection" : "delivery"));
  useEffect(() => { localStorage.setItem("cellar_delivery", method); }, [method]);
  const collection = method === "collection";
  const addressComplete = collection || Object.values(address).every((v) => String(v).trim().length > 0);
  const orderPayload = () => ({
    items: lines.map((l) => ({ product_handle: l.product_handle, variant_id: l.variant_id, quantity: l.quantity })),
    shipping_address: collection ? null : address,
    delivery_method: method,
  });

  const needAddress = () => {
    if (addressComplete) return false;
    toast.error("Add your delivery address first — or choose Collect in Melbourne.");
    document.querySelector<HTMLInputElement>('[data-testid="address-name-input"]')?.focus();
    return true;
  };
  const payByCard = async (useCredit = 0) => {
    if (needAddress()) return;
    setCardBusy(true);
    try {
      const { data } = await api.post("/stripe/checkout", { ...orderPayload(), origin_url: window.location.origin, use_credit_pence: useCredit });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errorMessage(e));
      setCardBusy(false);
    }
  };

  const checkout = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", orderPayload());
      clear();
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["addresses"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      toast.success(`Order ${data.order.shopify_order_name} placed — ${money(data.order.total_pence)} drawn from your cellar.`);
      if (data.auto_topup_payment) toast("Auto top-up submitted to GoCardless.", { description: money(data.auto_topup_payment.amount_pence) + " will be credited once confirmed." });
      navigate("/orders");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent aria-describedby={undefined} className="flex w-full flex-col bg-[var(--paper)] sm:max-w-md" data-testid="basket-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-3xl font-medium">Your case</SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto py-4">
          {lines.length === 0 && <p className="text-sm text-neutral-500" data-testid="basket-empty">Nothing in your case yet.</p>}
          {lines.map((l) => (
            <div key={l.variant_id} className="flex gap-4" data-testid={`basket-line-${l.variant_id}`}>
              <div className="h-20 w-14 shrink-0 overflow-hidden bg-[var(--parchment)]">{l.image && <img src={l.image} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1">
                <div className="text-sm font-medium leading-snug">{l.title}</div>
                {l.variant_title !== "Default Title" && <div className="text-xs text-neutral-500">{l.variant_title}</div>}
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => setQty(l.variant_id, l.quantity - 1)} className="border border-border p-1 hover:bg-[var(--parchment)]" data-testid={`basket-dec-${l.variant_id}`}><Minus className="h-3 w-3" /></button>
                  <span className="w-5 text-center text-sm tabular-nums" data-testid={`basket-qty-${l.variant_id}`}>{l.quantity}</span>
                  <button onClick={() => setQty(l.variant_id, l.quantity + 1)} className="border border-border p-1 hover:bg-[var(--parchment)]" data-testid={`basket-inc-${l.variant_id}`}><Plus className="h-3 w-3" /></button>
                  <button onClick={() => remove(l.variant_id)} className="ml-auto text-neutral-400 hover:text-[var(--wine)]" data-testid={`basket-remove-${l.variant_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.25} /></button>
                </div>
              </div>
              <div className="text-sm tabular-nums">{money(l.unit_price_pence * l.quantity)}</div>
            </div>
          ))}
          {lines.length > 0 && (
            <div className="space-y-3 border-t border-border pt-5">
              <div className="grid grid-cols-2 gap-px" role="radiogroup" data-testid="delivery-method-group">
                {([["delivery", "Deliver to me"], ["collection", "Collect in Melbourne"]] as const).map(([v, label]) => (
                  <button key={v} role="radio" aria-checked={method === v} onClick={() => setMethod(v)} className={`border py-3 text-sm transition-colors duration-200 ${method === v ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`delivery-method-${v}`}>{label}</button>
                ))}
              </div>
              {collection ? (
                <div className="border border-dashed border-border p-3 text-sm" data-testid="collection-details">
                  <div className="eyebrow">Collect from</div>
                  <div className="mt-1">{config?.collection_point?.name || "Pierre Hourlier Wines"}</div>
                  <div className="text-neutral-600">{config?.collection_point?.address1 || "Unit 11, Melbourne Hall Visitor Centre, Church Square"}, {config?.collection_point?.city || "Melbourne"} {config?.collection_point?.postcode || "DE73 8EN"}</div>
                  <div className="mt-1 text-xs text-neutral-500">No delivery charge. We'll email you when your wines are ready to pick up · {config?.collection_point?.phone || "01332 341466"}</div>
                </div>
              ) : (<>
              <div className="eyebrow">Deliver to</div>
              {saved && saved.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="saved-addresses">
                  {saved.map((a) => {
                    const active = a.address1 === address.address1 && a.postcode.replace(/\s/g, "").toUpperCase() === address.postcode.replace(/\s/g, "").toUpperCase();
                    return (
                      <span key={a.id} className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs transition-colors duration-200 ${active ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`}>
                        <button onClick={() => setAddress({ name: a.name, address1: a.address1, city: a.city, postcode: a.postcode })} data-testid={`saved-address-${a.id}`}>{a.address1}, {a.postcode}</button>
                        <button onClick={() => window.confirm(`Forget ${a.address1}, ${a.postcode}?`) && removeSaved(a.id)} aria-label="Forget address" className={`-mr-1 p-1 ${active ? "text-[var(--paper)]/70" : "text-neutral-400 hover:text-[var(--wine)]"}`} data-testid={`saved-address-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
              <Input placeholder="Full name" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} data-testid="address-name-input" />
              <Input placeholder="Address line" value={address.address1} onChange={(e) => setAddress({ ...address, address1: e.target.value })} data-testid="address-line-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Town / City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="address-city-input" />
                <Input placeholder="Postcode" value={address.postcode} onChange={(e) => setAddress({ ...address, postcode: e.target.value })} data-testid="address-postcode-input" />
              </div>
              </>)}
            </div>
          )}
        </div>
        {lines.length > 0 && (
          <div className="space-y-4 border-t border-border pt-5">
            {saving > 0 && <div className="flex justify-between text-sm text-[var(--moss)]"><span>{clearance?.reason || "Case saving"}</span><span className="tabular-nums" data-testid="basket-clearance-saving">−{money(saving)}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Case total</span><span className="tabular-nums" data-testid="basket-total">{money(total)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-neutral-500">Cellar balance</span><span className="tabular-nums" data-testid="basket-balance">{money(balance)}</span></div>
            {shortfall > 0 && (
              <p className="text-sm text-[var(--wine)]" data-testid="basket-shortfall">You need {money(shortfall)} more in your cellar to place this order.</p>
            )}
            {shortfall > 0 && config?.card_payments ? (<>
              {balance > 0 && (
                <Button className="h-auto w-full whitespace-normal rounded-none py-5 text-base" disabled={cardBusy} onClick={() => payByCard(balance)} data-testid="checkout-card-split-button">
                  <CreditCard className="mr-2 h-4 w-4 shrink-0" strokeWidth={1.25} />{cardBusy ? "Opening secure checkout…" : <span>Use my {money(balance)} credit, pay {money(shortfall)} by card</span>}
                </Button>
              )}
              <Button variant={balance > 0 ? "outline" : "default"} className="w-full rounded-none py-5" disabled={cardBusy} onClick={() => payByCard(0)} data-testid="checkout-card-button">
                <CreditCard className="mr-2 h-4 w-4" strokeWidth={1.25} />{cardBusy ? "Opening secure checkout…" : `Pay the full ${money(total)} by card${balance > 0 ? " · keep my credit" : ""}`}
              </Button>
              <p className="text-center text-xs text-neutral-500" data-testid="basket-card-note">Secure card payment by Stripe. Your order is placed the moment the payment goes through.</p>
              <Button variant="ghost" className="w-full rounded-none" onClick={() => { setOpen(false); navigate(`/?tab=topup&amount=${Math.ceil(shortfall / 100)}`); }} data-testid="basket-topup-link">Or add {money(shortfall)} credit to your cellar first</Button>
            </>) : (<>
              <Button className="w-full rounded-none py-6 text-base" disabled={placing || shortfall > 0} onClick={() => { if (!needAddress()) checkout(); }} data-testid="checkout-button">
                {placing ? "Placing order…" : shortfall > 0 ? "Insufficient cellar balance" : `Pay ${money(total)} from cellar`}
              </Button>
              {config?.card_payments && (
                <Button variant="outline" className="w-full rounded-none py-5" disabled={cardBusy} onClick={() => payByCard(0)} data-testid="checkout-card-button">
                  <CreditCard className="mr-2 h-4 w-4" strokeWidth={1.25} />{cardBusy ? "Opening secure checkout…" : `Pay ${money(total)} by card`}
                </Button>
              )}
              {shortfall > 0 && (
                <Button variant="ghost" className="w-full rounded-none" onClick={() => { setOpen(false); navigate(`/?tab=topup&amount=${Math.ceil(shortfall / 100)}`); }} data-testid="basket-topup-link">Add {money(shortfall)} credit</Button>
              )}
            </>)}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
